import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useSectionNav } from "@/components/Navbar";
import { useLang } from "@/i18n/LanguageContext";
import { useCurrency, PLAN_PRICE_BRL, EXTRA_DAY_BRL } from "@/i18n/CurrencyContext";

export default function Plans() {
  const { t } = useLang();
  const { currency, fmtPrice, fmtAddon } = useCurrency();
  const goSection = useSectionNav();
  const items = t("plans.items");

  return (
    <section id="planes" data-testid="plans-section" className="py-24 sm:py-32 bg-[#F1E7D6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#7A571C] mb-3">{t("plans.eyebrow")}</p>
            <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0B2E4A] text-balance">
              {t("plans.title")}
            </h2>
          </div>
          <p className="text-[#56636D] text-sm sm:text-base max-w-sm leading-relaxed">{t("plans.side")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {Array.isArray(items) && items.map((p, i) => {
            const featured = i === 1;
            return (
              <motion.article
                key={p.name}
                data-testid={`plan-card-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={`relative rounded-3xl p-8 sm:p-9 flex flex-col transition-all duration-300 hover:-translate-y-1.5 ${
                  featured
                    ? "bg-white border-2 border-[#D9A854] shadow-[0_28px_60px_-16px_rgba(11,46,74,0.35)] lg:-translate-y-3 z-10"
                    : "bg-white border border-[#E6DED1] shadow-[0_4px_20px_-2px_rgba(11,46,74,0.06)] hover:shadow-[0_16px_40px_-8px_rgba(11,46,74,0.16)]"
                }`}
              >
                {featured && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#0B2E4A] text-[#FAF6EE] text-[10px] font-bold uppercase tracking-[0.16em] px-4 py-1.5 rounded-full shadow-lg shadow-[#0B2E4A]/30 whitespace-nowrap">
                    {t("plans.badge")}
                  </span>
                )}
                <h3 className="font-display font-medium text-2xl text-[#0B2E4A]">
                  {p.name}
                </h3>
                <p className="text-sm mt-1.5 font-semibold text-[#56636D]">
                  {p.tagline}
                </p>
                <div className="mt-6 flex items-baseline gap-2 flex-wrap">
                  <span data-testid={`plan-price-${i}`} className="font-display font-medium text-4xl text-[#0B2E4A]">
                    {currency === "BRL" ? p.price : fmtPrice(PLAN_PRICE_BRL[i])}
                  </span>
                  <span className="text-xs font-semibold text-[#56636D]">{p.priceSub.replace("{extra}", fmtAddon(EXTRA_DAY_BRL[i]))}</span>
                </div>
                <ul className="mt-7 space-y-3.5 flex-1">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex gap-3 text-sm leading-relaxed">
                      <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center bg-[#D9A854]/15">
                        <Check size={12} className="text-[#7A571C]" />
                      </span>
                      <span className="text-[#56636D]">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-cta-${i}`}
                  onClick={() => goSection("contacto")}
                  className={`mt-8 w-full font-semibold px-6 py-3.5 rounded-full transition-all duration-200 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95 ${
                    featured
                      ? "bg-[#0B2E4A] hover:bg-[#1F4F67] text-[#FAF6EE] shadow-lg shadow-[#0B2E4A]/25"
                      : "bg-[#D9A854] hover:bg-[#C99944] text-[#0B2E4A]"
                  }`}
                >
                  {t("plans.cta")} <ArrowRight size={15} />
                </button>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <p data-testid="plans-note" className="text-sm text-[#56636D] italic">{t("plans.note")}</p>
          <Link
            to="/viaje-a-medida"
            data-testid="plans-more-link"
            className="text-sm font-bold text-[#0B2E4A] hover:text-[#7A571C] transition-colors underline underline-offset-4 whitespace-nowrap"
          >
            {t("plans.more")}
          </Link>
        </div>
      </div>
    </section>
  );
}
